import { useI18n } from 'vue-i18n'
import { clamp, toast } from '@/common/util'
import baseApi from './base.api'
import BaseService from './base.service'

export type fileType = 'video' | 'audio' | 'image' | 'scorm' | 'document' | 'other' | 'link' | 'pdf' | 'assessment' | 'carousel' | 'freecontent'| 'freeContent'

export const mediaTypes = [ 'video', 'audio', 'image', 'scorm', 'document', 'link', 'pdf', 'course', 'trail', 'carousel', 'freeContent' ]
export const mediaType2Api = [ 'video', 'audio', 'imagem', 'scorm', 'documento', 'link', 'pdf', 'curso', 'formacao', 'carrossel', 'conteudolivre' ]

export const allowedMedias = () => {
  return mediaTypes
    .map((type) => ({
      value: type,
      label: useI18n().t(`labels.${type}`)
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
}

export interface GetFilesOptions {
  search?: string
  //
  name?: string
  active?: boolean
  code?: string
  types?: string[]
  competenceIds?: string[]
  categoryIds?: string[]
  //
  page?: number
  size?: number
  orderBy?: { prop: string, order: 'ASC' | 'DESC' }[]
}

export interface GetFilesResponse {
  id: string
  name: string
  description: string
  pullzone: string
  videoId: string
  expirationDate: string | Date | null
  duration: string
  fileType: fileType
  categoryId: string | null
  categoryName: string | null
  competenciesIds: string[]
  sectionsIds: string[]
  link: string
  createdAt: string | Date
  lastUpdatedAt: string | Date
  finished: boolean
  active: boolean
  tenantId: string
  origin: string
  objectCode: string
  coverImageUrl: string | null
  hlsPlaylistUrl: string | null
  scormScriptUrl: string | null
  scormType: string | null
  allowDownload: boolean
  waterMarkRules?: {
    enabled?: boolean
    username?: boolean
    email?: boolean
    company?: boolean
    unit?: boolean
    department?: boolean
    position?: boolean
    dateTime?: boolean
    ip?: boolean
  }
  freeContent?: string
}

const normalizeType = (type: string, toApi?: boolean) => {
  // From api to app
  if (!toApi) return mediaTypes[mediaType2Api.indexOf(type)] || 'other'
  return mediaType2Api[mediaTypes.indexOf(type)] || 'outros'
}

type QuickSearchResult = {
  id: string
  titulo: string
  descricao: string
  sinopse: string
  urlDaImagem: string
  tipo: string
}

export type QuickSearchData = {
  id: string
  title: string
  description: string
  synopsis: string
  thumbnail: string
  type: string
}

export const midiaService = {
  ...baseApi,
  resource: 'midia',
  search(term: string, options?: any) {
    const normalize = (data: QuickSearchResult) => ({
      id: data.id,
      title: data.titulo,
      description: data.descricao,
      synopsis: data.sinopse,
      thumbnail: data.urlDaImagem,
      type: normalizeType(data.tipo.toLowerCase())
    })

    return midiaService.get<any>('quickSearch', { search: term }, options)
      .then(({ success, data }) => ({
        success,
        data: {
          files: data.midias.map(normalize) as QuickSearchData[],
          courses: data.cursos.map(normalize) as QuickSearchData[],
          trails: data.formacoes.map(normalize) as QuickSearchData[]
        }
      }))
      .catch((error) => {
        if(error.code === 'ERR_CANCELED') return error

        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao tentar buscar informações sobre o arquivo!' })

        return {
          success: false,
          data: {
            files: [] as QuickSearchData[],
            courses: [] as QuickSearchData[],
            trails: [] as QuickSearchData[]
          }
        }
      })
  },
  getFile(id: string, options?: any) {
    return midiaService
      .get<{
        id: string
        nome: string
        descricao: string
        hlsPlaylistURL: string | null
        pullzone: string | null
        videoId: string | null
        dataExpiracao: string | null
        duracao: string
        tipoArquivo: string
        categoriaId: string | null
        categoriaNome: string | null
        competenciasIds: string[]
        secoesIds: string[]
        link: string
        createdAt: string
        lastUpdatedAt: string
        finalizado: boolean
        active: boolean
        scormScriptLink: string | null
        tenantId: string
        origem: string
        codigoObjeto: string
        imagemDoCardHorizontal: string | null
        imagemDoCardVertical: string | null
        tipoScorm: string | null
        permitirDownload: boolean
        legendas?: {
          id: string
          nomeLegenda: string
          codigoLegenda: string
          urlLegenda: string
          createdAt: string
        }[],
        bloquearAvanco: boolean
        pdfMarcadAguaConfig?: {
          user: {
            name: boolean
            email: boolean
            company: boolean
            businessUnit: boolean
            department: boolean
            role: boolean
            waterMarkRegistrationDate: boolean
            ip: boolean
          },
          includeWaterMarkInPDF: boolean
        }
        carrossel?: {
          id: string
          link: string
          ordem: number
        }[] | null
        conteudoLivre?: string
        contextoDaIA?: string
      }>(id, options)
      .then(({ success, data }) => ({
        success,
        data: {
          id: data.id,
          name: data.nome,
          description: data.descricao,
          pullzone: data.pullzone,
          videoId: data.videoId,
          expirationDate: data.dataExpiracao,
          duration: data.duracao,
          fileType: normalizeType(data.tipoArquivo?.toLowerCase()) as fileType,
          categoryId: data.categoriaId,
          categoryName: data.categoriaNome,
          competenciesIds: data.competenciasIds,
          sectionsIds: data.secoesIds,
          link: data.link,
          createdAt: data.createdAt,
          lastUpdatedAt: data.lastUpdatedAt,
          finished: data.finalizado,
          active: data.active,
          tenantId: data.tenantId,
          origin: data.origem,
          objectCode: data.codigoObjeto,
          cardImgUrl: data.imagemDoCardHorizontal,
          cardHorizontalUrl: data.imagemDoCardHorizontal,
          cardVerticalUrl: data.imagemDoCardVertical,
          hlsPlaylistUrl: data.hlsPlaylistURL,
          scormScriptUrl: data.scormScriptLink,
          scormType: data.tipoScorm,
          downloadable: data.permitirDownload,
          disableFastForward: data.bloquearAvanco,
          subtitles: data.legendas?.map(sub => ({
            id: sub.id,
            name: sub.nomeLegenda,
            srclang: sub.codigoLegenda,
            url: sub.urlLegenda,
            createdAt: sub?.createdAt || ''
          })),
          watermark: data.pdfMarcadAguaConfig?.includeWaterMarkInPDF,
          watermarkRules: {
            username: data.pdfMarcadAguaConfig?.user.name,
            email: data.pdfMarcadAguaConfig?.user.email,
            company: data.pdfMarcadAguaConfig?.user.company,
            unit: data.pdfMarcadAguaConfig?.user.businessUnit,
            department: data.pdfMarcadAguaConfig?.user.department,
            position: data.pdfMarcadAguaConfig?.user.role,
            // dateTime: data.pdfMarcadAguaConfig?.user.dateTime,
            ip: data.pdfMarcadAguaConfig?.user.ip
          },
          carousel: data.tipoArquivo?.toLowerCase() === 'carrossel' ? data.carrossel?.map((item) => ({
            id: item.id,
            url: item.link,
            order: item.ordem,
          })) : null,
          covers: {
            horizontal: data.imagemDoCardHorizontal || null,
            vertical: data.imagemDoCardVertical || null
          },
          freeContent: data.conteudoLivre,
          aiContext: data.contextoDaIA
        }
      }))
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao tentar buscar informações sobre o arquivo!' })

        return { success: false, data: null }
      })
  },
  async getFiles(opt: GetFilesOptions, options?: any) {
    opt = { ...opt }

    const paramsDictionary = {
      active: 'ativo',
      name: 'nomeCompleto',
      search: 'search',
      types: 'tiposArquivos',
      code: 'codigoObjeto',
      competencies: 'competenciasIds',
      categoryIds:'categoriasIds',
      origin: 'origem',
      // page: 'page',
      // size: 'size'
    }

    // Adicione aqui os tipos (para filtros) como a API espera
    const typeDictionary = {
      image: 'imagem',
      document: 'documento',
      carousel: 'carrossel',
      freeContent: 'conteudoLivre'
    }

    const orderDictionary = {
      name: 'Midia',
      categoryName: 'Categoria',
      active: 'Active',
      objectCode: 'CodigoObjeto',
      duration: 'Duracao',
      createdAt: 'createdAt'
    }

    const extraData = {}

    Object.keys(opt).forEach((key) => {
      if (typeof paramsDictionary[key] !== 'undefined' && typeof opt[key] !== 'undefined' && opt[key] !== null)
        extraData[paramsDictionary[key]] = opt[key]

      if (key === 'types' && typeof extraData[paramsDictionary[key]] === 'object') {
        extraData[paramsDictionary[key]] = Object.values(opt[key]).map((item) => typeDictionary[item] || item)
      }
    })

    // Ordenação
    if (opt.orderBy?.length) {
      extraData['Ordenacao'] = opt.orderBy.map((item) => `${orderDictionary[item.prop]}${item.order === 'DESC' ? 'Desc' : 'Asc'}`)
    }

    return midiaService
      .get<any>('', extraData,  options)
      .then((res) => ({
        status: res.success,
        page: res.pageNumber,
        size: res.pageSize,
        total: res.totalItems,
        pages: res.totalPages,
        data: res.data?.map((item) => ({
          id: item.id,
          name: item.nome,
          description: item.descricao,
          pullzone: item.pullzone,
          videoId: item.videoId,
          expirationDate: item.dataExpiracao,
          duration: item.duracao,
          fileType: normalizeType(item.tipoArquivo?.toLowerCase()) as fileType,
          categoryId: item.categoriaId,
          categoryName: item.categoriaNome,
          competenciesIds: item.competenciasIds,
          sectionsIds: item.secoesIds,
          link: item.link,
          createdAt: item.createdAt,
          lastUpdatedAt: item.lastUpdatedAt,
          finished: item.finalizado,
          active: item.active,
          tenantId: item.tenantId,
          origin: item.origem,
          objectCode: item.codigoObjeto,
          coverImageUrl: item.linkDaImagemDeCapa,
          hlsPlaylistUrl: item.hlsPlaylistURL,
          scormScriptUrl: item.scormScriptLink,
          scormType: item.tipoScorm,
          allowDownload: item.permitirDownload,
          waterMarkRules: {
            enabled: item?.pdfMarcadAguaConfig?.includeWaterMarkInPDF || false,
            username: item?.pdfMarcadAguaConfig?.user.name || false,
            email: item?.pdfMarcadAguaConfig?.user.email || false,
            company: item?.pdfMarcadAguaConfig?.user.company || false,
            unit: item?.pdfMarcadAguaConfig?.user.unit || item?.pdfMarcadAguaConfig?.user.businessUnit || false,
            department: item?.pdfMarcadAguaConfig?.user.department || false,
            position: item?.pdfMarcadAguaConfig?.user.position || item?.pdfMarcadAguaConfig?.user.role || false,
            dateTime: item?.pdfMarcadAguaConfig?.user.dateTime || false,
            ip: item?.pdfMarcadAguaConfig?.user.ip || false
          },
          carousel: item.tipoArquivo?.toLowerCase() === 'carrossel' ? item.carrossel?.map((carouselItem) => ({
            id: carouselItem.id,
            url: carouselItem.link,
            order: carouselItem.ordem
          })) : null,
          freeContent: item.tipoArquivo?.toLowerCase() === 'conteudoLivre' ? item.conteudoLivre : null
        })) as GetFilesResponse[]
      }))
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao tentar buscar informações sobre os arquivos!' })

        return { status: false, data: [] as GetFilesResponse[] }
      })
  },
  save(data: any) {
    const saveData = {
      nome: data.name,
      descricao: data.description,
      hlsPlaylistURL: data.hlsPlaylistUrl,
      pullzone: data.pullzone,
      videoId: data.videoId,
      dataExpiracao: data.expirationDate,
      duracao: data.duration,
      tipoArquivo: normalizeType(data.fileType, true),
      categoriaId: data.categoryId,
      competenciasIds: data.competenciesIds,
      secoesIds: data.sectionsIds,
      codigoObjeto: data.objectCode,
      link: data.link,
      finalizado: data.finished,
      active: data.active,
      linkDaImagemDeCapa: data.cardImgUrl,
      scormScriptLink: data.scormScriptUrl,
      tipoScorm: data.scormType,
      permitirDownload: data.downloadable,
      bloquearAvanco: data.disableFastForward,
      // Marca d'agua e download
      pdfMarcadAguaConfig: {
        includeWaterMarkInPDF: data.watermark,
        user: {
          name: data.watermarkRules.username,
          email: data.watermarkRules.email,
          company: data.watermarkRules.company,
          businessUnit: data.watermarkRules.unit,
          department: data.watermarkRules.department,
          role: data.watermarkRules.position,
          waterMarkRegistrationDate: data.watermarkRules.dateTime,
          ip: data.watermarkRules.ip
        }
      },
      conteudoLivre: data.freeContent,
      contextoDaIA: data.aiContext
    }

    let req = null

    if (data?.id) {
      req = midiaService.put(data.id, saveData)
    } else {
      req = midiaService.post('', saveData)
    }

    return req.catch(() => {
      return { success: false, data: null }
    })
  },
  uploadSubtitle(data: {
    id?: string // subtitleId
    sourceId?: string // mediaId
    srclang: string
    file: File
  }, progressCB?: any) {
    const formData = {
      legenda: data.file,
      codigoLegenda: data.srclang,
      nomeLegenda: data.file.name
    }

    if (data.id) {
      formData['midiaId'] = data.sourceId
    }

    const options = {
      onUploadProgress: (progressEvent) => {
        if (progressCB) progressCB(progressEvent)
      }
    }

    let req = !data.id
      ? midiaService.post(`uploadLegenda/${data.sourceId}`, formData, options, true)
      : midiaService.put(`updateLegenda/${data.id}`, formData, options, true)

    return req
      .then((res) => res)
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao tentar enviar a legenda!' })

        return { success: false, data: null }
      })
  },
  removeSubtitle(id: string) {
    return midiaService.delete(`deleteLegenda/${id}`)
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao tentar remover a legenda!' })

        return { success: false, data: null }
      })
  },
  // File upload
  fileUpload(depotId: string, file: File, extraParams: any) {
    return midiaService.post(`big/${depotId}/fileUpload`, {
      arquivo: file,
      midiaId: depotId
    }, extraParams, true)
  },
  cardCoverUpload: (id: string, file: File, extraParams?: any) => {
    return midiaService.post(`${id}/fileUpload/CardHorizontal`, {
      file,
      midiaId: id
    }, extraParams, true)
  },
  coverUpload(id: string, file, orientation: 'horizontal' | 'vertical') {
    const type = orientation === 'horizontal' ? 'CardHorizontal' : 'CardVertical'

    const formData = new FormData()
    formData.append('file', file)

    return midiaService.post(`${id}/fileUpload/${type}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  videoCoverUpload: (videoId: string, file: File, extraParams?: any) => {
    return midiaService.post(`uploadThumbnail/${videoId}`, {
      file,
      name: file.name || null
    }, extraParams, true)
  },
  getFileInfo(id: string) {
    return midiaService
      .get(`getMidiaInfo/${id}`)
      .then(({ success, data: res } : any) => {
        let type = normalizeType(res?.tipoArquivo.toLowerCase()) as fileType

        let source : string = res.link

        if (type === 'video') {
          source = `//${res.pullzone}/${res.videoId}/playlist.m3u8`
        } else if (type === 'scorm') {
          source = res.scormScriptLink
        }

        return {
          success,
          data: {
            id: res?.id as string,
            name: res?.nome as string,
            description: res?.descricao as string,
            duration: res?.duracao as string,
            source,
            videoId: res?.videoId as string | null,
            pullzone: res?.pullzone as string | null,
            type,
            expiration: res?.dataExpiracao ? (new Date(res.dataExpiracao)) : null,
            allowDownload: res.permitirDownload as boolean,
            blockFastForward: res.bloquearAvanco as boolean,
            watermark: res?.pdfMarcadAguaConfig?.includeWaterMarkInPDF as boolean,
            watermarkRules: {
              username: res?.pdfMarcadAguaConfig?.user.name,
              email: res?.pdfMarcadAguaConfig?.user.email,
              company: res?.pdfMarcadAguaConfig?.user.company,
              unit: res?.pdfMarcadAguaConfig?.user.businessUnit,
              department: res?.pdfMarcadAguaConfig?.user.department,
              position: res?.pdfMarcadAguaConfig?.user.role,
              // dateTime: data.pdfMarcadAguaConfig?.user.dateTime,
              ip: res?.pdfMarcadAguaConfig?.user.ip
            },
            carousel: res?.carrossel?.map((item) => item.link) || [],
            freeContent: res?.conteudoLivre as string | null
          }
        }
      })
  },
  getVideoInfo(videoId: string) {
    return midiaService
      .get<{
        availableResolutions: string
        captions: any[]
        chapters: any[]
        encodeProgress: number
        guid: string
        hasMP4Fallback: boolean
        width: number
        height: number
        metaTags: any[]
        moments: any[]
        thumbnailFileName: string
        title: string
        transcodingMessages: any[],
        status: number
      }>(`GetVideoInfo/${videoId}`)
      .then(({ success, data }) => {
        return {
          success,
          data
        }
      })
  },
  setVideoInfo(
    midiaId: string,
    data: {
      title?: string
      chapters?: {
        title: string
        start: number
        end: number
      }[]
      moments?: {
        label: string
        timestamp: number
      }[]
      metaTags?: {
        property: string
        value: string
      }
    }) {
    return midiaService
      .post(`updateVideoInfo/${midiaId}`, data)
      .then(({ success, data }) => {
        return {
          success,
          data
        }
      })
  },

  async bookmark(midiaId: string, remove?: boolean) {
    return midiaService.post([midiaId, !remove ? 'bookmark' : 'unbookmark'].join('/'))
  },

  async unbookmark(midiaId: string) {
    return await midiaService.post([midiaId, 'unbookmark'].join('/'))
  },

  async setReactionMedia(params: { id: string, reacao?: string }) {
    return await midiaService.post(`SetReaction`, params)
  },

  getDetalhes(id: string, ignoreFilterSection: boolean = false) {
    return midiaService
      .get<{
        id: string
        nome: string
        descricao: string
        hlsPlaylistURL: string | null
        pullzone: string | null
        videoId: string | null
        dataExpiracao: string | null
        duracao: string
        tipoArquivo: string
        categoriaId: string | null
        categoriaNome: string | null
        competenciasIds: string[]
        secoesIds: string[]
        link: string
        createdAt: string
        lastUpdatedAt: string
        finalizado: boolean
        active: boolean
        scormScriptLink: string | null
        tenantId: string
        origem: string
        codigoObjeto: string
        linkDaImagemDeCapa: string | null
        tipoScorm: string | null
        permitirDownload: boolean
        legendas?: {
          id: string
          nomeLegenda: string
          codigoLegenda: string
          urlLegenda: string
          createdAt: string
        }[],
        bloquearAvanco: boolean
        pdfMarcadAguaConfig?: {
          user: {
            name: boolean
            email: boolean
            company: boolean
            businessUnit: boolean
            department: boolean
            role: boolean
            waterMarkRegistrationDate: boolean
            ip: boolean
          },
          includeWaterMarkInPDF: boolean
        },
        conteudoLivre?: string
      }>(`Detalhes/${id}`, { ignoreFilterSection })
      .then(({ success, data }) => ({
        success,
        data
      }))
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao tentar buscar informações sobre o arquivo!' })

        return { success: false, data: null }
      })
  },

  async authorizeUpload(tituloDoVideo: string) {
    return await midiaService
      .post<{
        videoId: string
        libraryId: string
        authorizationSignature: string
        authorizationExpire: number
        embedLink: string
      }>(`AuthorizeUpload`,  tituloDoVideo)
      .then(({ success, data }) => ({
        success,
        data
      }))
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao preparar o upload direto do vídeo!' })

        return { success: false, data: null }
      })
  },

  async syncUpload(midiaId: string, videoId: string, link: string) {
    const payload = {
      videoId,
      link
    }

    return await midiaService
      .put<{ }>(`${midiaId}/SyncUpload`, payload)
      .then(({ success, data }) => ({
        success,
        data
      }))
      .catch((error) => {
        const { notifications }: { notifications: string[] } = error.response?.data || {}
        toast.error({ message: notifications[0] || 'Erro ao sincronizar o upload!' })

        return { success: false, data: null }
      })
  }
}

class MidiasService extends BaseService {
  constructor() {
    super({ resource: 'midia' })
  }

  async fileUploadCoverImage(id: string, file, params: { progressCB?: any; controller?: any }) {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('midiaId', id)

    const { progressCB, controller } = params
    const signal = controller ? { signal: controller.signal } : {}

    return await this.plantarApi.post(`${this.resource}/${id}/fileUpload`, formData, {
      ...signal,
      onUploadProgress: (progressEvent) => {
        if (progressCB) progressCB(progressEvent)
      }
    })
  }

  fileUpload(id: string, file, params: { progressCB?: any; controller?: any }) {
    const formData = new FormData()

    formData.append('arquivo', file)
    formData.append('midiaId', id)

    const { progressCB, controller } = params
    const signal = controller ? { signal: controller.signal } : {}

    return this.plantarApi.post(`${this.resource}/big/${id}/fileUpload`, formData, {
      ...signal,
      onUploadProgress: (progressEvent) => {
        if (progressCB) progressCB(progressEvent)
      }
    })
  }

  async fileDownload(id: string, params: { progressCB?: any; controller?: any }) {
    const { progressCB, controller } = params
    const signal = controller ? { signal: controller.signal } : {}

    return await this.plantarApi.get(`${this.resource}/${id}/downloadFile`, {
      responseType: 'blob',
      ...signal,
      timeout: 0,
      onDownloadProgress: (progressEvent) => {
        if (progressCB) progressCB(progressEvent)
      }
    })
  }

  registerProgress(id: string, progress: number, { isClassroom, inscriptionId, journeyId = null }) {
    const postData = {
      midiaId: id,
      percentualProgresso: clamp(Number(progress), 0, 100),
      inscricaoId: inscriptionId,
      isSalaDeAula: isClassroom ?? false,
      jornadaId: journeyId || null
    }

    return this.plantarApi.post(`${this.resource}/progresso`, postData)
  }

  async getProgress(type: string, id: string) {
    return await this.plantarApi.get(`${this.resource}/progresso/${type}/${id}`)
  }

  async getBonds(midiaId: string) {
    return this.plantarApi.get(`${this.resource}/vinculos/${midiaId}`)
  }

  async getVideoInfo(videoId: string, params: any) {
    try {
      return await this.plantarApi.get(`${this.resource}/GetVideoInfo/${videoId}`, { ...params })
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar buscar informações sobre o vídeo!' })
    }
  }
}

const midiasService = new MidiasService()

export default midiasService
